/**
 * SVGZ asset loader with global URL-keyed deduplication cache.
 *
 * Fetches SVGZ files, decompresses them via the native DecompressionStream API,
 * and caches the resulting SVG text globally — each URL is fetched at most once
 * per page load regardless of how many overlays reference it.
 *
 * @module mapLibrary/svgzLoader
 */

// Global cache: URL → Promise<string|null> (decompressed SVG text, or null on fetch failure)
const svgLayerTextCache = new Map();

/**
 * Returns true when the text begins with an <svg> root element, allowing leading
 * whitespace, an optional XML prolog, and any number of comments before it.
 * Implemented as a linear scan instead of a single regex to avoid catastrophic
 * backtracking on inputs with many repeated comment delimiters.
 */
function startsWithSvgRoot(text) {
  let i = 0;
  const skipWhitespace = () => {
    while (i < text.length && /\s/.test(text[i])) {
      i += 1;
    }
  };

  skipWhitespace();
  if (text.slice(i, i + 5).toLowerCase() === '<?xml') {
    const prologEnd = text.indexOf('?>', i + 5);
    if (prologEnd === -1) {
      return false;
    }
    i = prologEnd + 2;
    skipWhitespace();
  }

  while (text.startsWith('<!--', i)) {
    const commentEnd = text.indexOf('-->', i + 4);
    if (commentEnd === -1) {
      return false;
    }
    i = commentEnd + 3;
    skipWhitespace();
  }

  return /^<svg[\s>]/i.test(text.slice(i, i + 5));
}

/**
 * Fetches a SVGZ URL, decompresses it via the native DecompressionStream API,
 * and returns the SVG text. Results are cached globally — each URL is fetched at most once.
 *
 */
export default function fetchSvgzLayerText(url) {
  // No asset to load (e.g. the API Gateway returned an empty mapBlobUrl on upstream
  // failure) — fetch("") would otherwise re-request the current page itself, whose
  // HTML can false-positive the <svg> content sniff below and get injected as if it
  // were the overlay asset.
  if (!url) {
    return Promise.resolve(null);
  }

  if (!svgLayerTextCache.has(url)) {
    const loadPromise = (async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          // Non-OK responses should be retriable on later calls.
          svgLayerTextCache.delete(url);
          return null;
        }

        // Prefer the simple path first. This matches prior behavior and works when
        // the browser/server already handles content decompression.
        const svgText = await response.text();
        // Anchored to the start (after optional leading whitespace/XML prolog) so an
        // unrelated HTML document that merely contains an inline <svg> icon somewhere
        // (e.g. a mis-resolved URL returning a normal page) isn't mistaken for the asset.
        if (startsWithSvgRoot(svgText)) {
          return svgText;
        }

        const canDecompress = typeof window !== 'undefined' &&
          typeof window.DecompressionStream === 'function';
        if (!canDecompress) {
          return null;
        }

        // Fallback: fetch again and attempt explicit gzip decompression for SVGZ payloads.
        const compressedResponse = await fetch(url);
        if (!compressedResponse.ok || !compressedResponse.body) {
          svgLayerTextCache.delete(url);
          return null;
        }

        const decompressedStream = compressedResponse.body.pipeThrough(
          new window.DecompressionStream('gzip')
        );
        return new Response(decompressedStream).text();
      } catch {
        // Network/CORS failures should not permanently poison the cache.
        svgLayerTextCache.delete(url);
        return null;
      }
    })();
    svgLayerTextCache.set(url, loadPromise);
  }
  return svgLayerTextCache.get(url);
}
